import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import SearchableSelect from '../components/SearchableSelect';
import UserPermissionsModal from '../components/UserPermissionsModal';
import RolePermissionsSelector from '../components/RolePermissionsSelector';
import Pagination from '../components/Pagination';
import { 
  UserGroupIcon,
  UserIcon,
  KeyIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from '../utils/toast';

const AdminRolesPermissions = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserPermissionsModal, setShowUserPermissionsModal] = useState(false);
  const [showUserRoleModal, setShowUserRoleModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [userRoles, setUserRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [activeTab, setActiveTab] = useState('roles'); // 'roles', 'permissions', 'users'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.organization_name && user.organization_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Paginated users for the users tab
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        toast.error('Admin authentication required');
        return;
      }

      const [rolesResponse, permissionsResponse, usersResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/roles-with-permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!rolesResponse.ok || !permissionsResponse.ok || !usersResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const [rolesData, permissionsData, usersData] = await Promise.all([
        rolesResponse.json(),
        permissionsResponse.json(),
        usersResponse.json()
      ]);

      setRoles(rolesData.roles || []);
      setPermissions(permissionsData.permissions || {});
      setUsers(usersData.users || []);
      setFilteredUsers(usersData.users || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load roles and permissions data');
    } finally {
      setLoading(false);
    }
  };

  const openUserPermissionsModal = (user) => {
    setSelectedUser(user);
    setShowUserPermissionsModal(true);
  };

  const openUserRoleModal = async (user) => {
    try {
      setSelectedUser(user);
      setShowUserRoleModal(true);
      
      const token = localStorage.getItem('admin_token');
      const [rolesResponse, permissionsResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${user.id}/roles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${user.id}/permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (rolesResponse.ok) {
        const rolesData = await rolesResponse.json();
        const roleIds = rolesData.user_roles.map(role => role.id);
        setSelectedRoleIds(roleIds);
        setUserRoles(rolesData.user_roles);
      }

      if (permissionsResponse.ok) {
        const permissionsData = await permissionsResponse.json();
        const allPermissions = [
          ...(permissionsData.permissions?.role_based || []),
          ...(permissionsData.permissions?.direct_grants || [])
        ];
        const permissionNames = allPermissions.map(p => p.permission_name);
        setSelectedPermissions(permissionNames);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to load user data');
    }
  };

  const handleUpdateUserRoles = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('admin_token');
      
      // Use the existing bulk role update endpoint for now
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users/${selectedUser.id}/roles`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role_ids: selectedRoleIds
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update user roles');
      }

      toast.success(`🎉 Successfully updated roles and permissions for ${selectedUser.first_name} ${selectedUser.last_name}!`);
      setShowUserRoleModal(false);
      await fetchData(); // Refresh data
      
    } catch (error) {
      console.error('Error updating user roles:', error);
      toast.error('Failed to update user roles. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoleIds(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };


  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'super_admin': 'bg-red-100 text-red-800 border-red-200',
      'admin': 'bg-orange-100 text-orange-800 border-orange-200',
      'manager': 'bg-blue-100 text-blue-800 border-blue-200',
      'editor': 'bg-green-100 text-green-800 border-green-200',
      'viewer': 'bg-gray-100 text-gray-800 border-gray-200',
      'agent_creator': 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[roleName] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800 border-green-200',
      'inactive': 'bg-gray-100 text-gray-800 border-gray-200',
      'suspended': 'bg-red-100 text-red-800 border-red-200',
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading roles and permissions...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Roles & Permissions Management</h1>
                  <p className="text-sm text-gray-600">Manage system roles, permissions, and user assignments</p>
                </div>
              </div>
              <button
                onClick={fetchData}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <nav className="-mb-px flex space-x-8">
              {[
                { id: 'roles', label: 'System Roles', icon: UserGroupIcon },
                { id: 'permissions', label: 'Permissions', icon: KeyIcon },
                { id: 'users', label: 'User Assignments', icon: UserIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Content */}
        {activeTab === 'roles' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">System Roles</h2>
              <p className="text-sm text-gray-600 mt-1">Overview of all system roles and their configurations</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {roles.map((role) => (
                  <div key={role.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">{role.display_name}</h3>
                          {role.is_system_role && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              System
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-4">{role.description}</p>
                        
                        <div className="space-y-3">
                          <div className="flex items-center text-sm text-gray-500">
                            <KeyIcon className="h-4 w-4 mr-2" />
                            <span>{role.permissions_count || role.permission_count || 0} permissions</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <UserIcon className="h-4 w-4 mr-2" />
                            <span>{role.user_count} users assigned</span>
                          </div>
                          
                          {/* Permissions Preview */}
                          {role.permissions && Object.keys(role.permissions).length > 0 && (
                            <div className="border-t border-gray-200 pt-3">
                              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                                Permissions by Category
                              </h4>
                              <div className="space-y-2 max-h-32 overflow-y-auto">
                                {Object.keys(role.permissions).map(category => (
                                  <div key={category} className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-medium text-gray-600">{category}</span>
                                      <span className="text-xs text-gray-400">
                                        {role.permissions[category].length}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {role.permissions[category].slice(0, 3).map(permission => (
                                        <span key={permission.name} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700" title={permission.description}>
                                          {permission.display_name}
                                        </span>
                                      ))}
                                      {role.permissions[category].length > 3 && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                                          +{role.permissions[category].length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">System Permissions</h2>
              <p className="text-sm text-gray-600 mt-1">All available permissions organized by category</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {Object.entries(permissions).map(([category, categoryPermissions]) => (
                  <div key={category} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded ${expandedCategories[category] ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <KeyIcon className={`h-5 w-5 ${expandedCategories[category] ? 'text-blue-600' : 'text-gray-600'}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{category}</h3>
                          <p className="text-sm text-gray-500">{categoryPermissions.length} permissions</p>
                        </div>
                      </div>
                      {expandedCategories[category] ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedCategories[category] && (
                      <div className="border-t border-gray-200 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {categoryPermissions.map((permission) => (
                            <div key={permission.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                              <div className="flex-1">
                                <p className="font-medium text-sm text-gray-900">{permission.display_name}</p>
                                <p className="text-xs text-gray-600">{permission.description}</p>
                                <p className="text-xs text-gray-400 mt-1 font-mono">{permission.name}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">User Role Assignments</h2>
                  <p className="text-sm text-gray-600 mt-1">Manage role assignments for all users</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Roles</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-800">
                                {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.organization_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusBadgeColor(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {user.roles && user.roles.length > 0 ? (
                            user.roles.map((role) => (
                              <span
                                key={role.id}
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(role.name)}`}
                              >
                                {role.display_name}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500 italic">No roles assigned</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openUserRoleModal(user)}
                            className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                            title="Manage roles"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Manage Roles
                          </button>
                          <button
                            onClick={() => openUserPermissionsModal(user)}
                            className="inline-flex items-center px-3 py-1.5 border border-indigo-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                            title="Manage permissions"
                          >
                            <KeyIcon className="h-4 w-4 mr-1" />
                            Permissions
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={filteredUsers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              showItemCount={true}
            />
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? 'Try adjusting your search terms.' : 'No users are available.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* User Permissions Modal */}
        <UserPermissionsModal
          user={selectedUser}
          isOpen={showUserPermissionsModal}
          onClose={() => setShowUserPermissionsModal(false)}
          onUpdate={fetchData}
          isAdmin={true}
        />

        {/* User Role Assignment Modal */}
        {showUserRoleModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Manage Roles - {selectedUser?.first_name} {selectedUser?.last_name}
                </h3>
                <button
                  onClick={() => setShowUserRoleModal(false)}
                  disabled={submitting}
                  className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mt-6 space-y-6">
                <RolePermissionsSelector
                  selectedRoles={selectedRoleIds}
                  onRolesChange={setSelectedRoleIds}
                  selectedPermissions={selectedPermissions}
                  onPermissionsChange={setSelectedPermissions}
                  availableRoles={roles}
                  availablePermissions={permissions}
                />
              </div>
              
              <div className="mt-6 flex justify-end space-x-3 pt-6">
                <button
                  onClick={() => setShowUserRoleModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUserRoles}
                  disabled={submitting}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2 min-w-[100px] justify-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRolesPermissions;